import React from "react";
import styled from "styled-components";

import { borderRadius } from "~/styles/mixins";
import { blue } from "~/styles/theme/color";

export interface BaseTabProps {
  index: number;
  error?: boolean;
  label?: React.ReactNode;
  selected?: boolean;
  onClick?: (event: React.MouseEvent<HTMLButtonElement>) => void;
}

export const StyledTab = React.forwardRef<HTMLButtonElement, BaseTabProps>(
  function StyledTab({ index, label, selected, onClick }, ref) {
    return (
      <StyledTabButton
        ref={ref}
        id={`tab-${index}`}
        role="tab"
        type="button"
        aria-selected={selected}
        $selected={selected}
        onClick={onClick}
      >
        {label}
      </StyledTabButton>
    );
  }
);

const StyledTabButton = styled.button<{ $selected?: boolean }>`
  appearance: none;
  border: none;
  background: none;
  cursor: pointer;
  min-height: 44px;
  min-width: 96px;
  padding: 0 16px;
  position: relative;
  z-index: 1;
  color: ${blue};
  text-transform: initial;
  font-weight: ${(props) => (props.$selected ? "bold" : "normal")};

  &:hover {
    opacity: 1;
  }

  &:focus {
    outline: none;
  }

  ${(props) =>
    props.$selected &&
    `
    &:after {
      content: "";
      display: block;
      position: absolute;
      top: 3px;
      left: 4px;
      right: 4px;
      bottom: 3px;
      ${borderRadius()};
      background-color: #fff;
      box-shadow: 0 4px 12px 0 rgba(0, 0, 0, 0.16);
      z-index: -1;
    }
  `}
`;

interface TabsProps {
  value: number;
  onChange: (event: React.SyntheticEvent, value: number) => void;
  children: React.ReactNode;
}

export const StyledTabs = React.forwardRef<HTMLDivElement, TabsProps>(
  function StyledTabs({ value, onChange, children }, ref) {
    return (
      <StyledTabsContainer ref={ref} role="tablist" tabIndex={-1}>
        {React.Children.map(children, (child) => {
          if (!React.isValidElement<BaseTabProps>(child)) {
            return child;
          }
          return React.cloneElement(child, {
            selected: child.props.index === value,
            onClick: (event: React.MouseEvent<HTMLButtonElement>) =>
              onChange(event, child.props.index),
          });
        })}
      </StyledTabsContainer>
    );
  }
);

const StyledTabsContainer = styled.div`
  display: inline-flex;
  background-color: #eee;
  min-height: 44px;

  ${borderRadius()};
`;
